import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { firstValueFrom, take, timeout } from 'rxjs';
import { PushHttpService } from './push-http.service';
import { PushNotificationData, PushPlatform, PushSubscriptionPayload } from './push.types';

export type PushBackendStatus = 'unknown' | 'available' | 'unavailable';

export class PushPermissionDeniedError extends Error {
    constructor() {
        super('push-permission-denied');
    }
}

/** `swPush.subscription` never emits when the worker is disabled — cap the wait. */
const SUBSCRIPTION_WAIT_MS = 5000;

/**
 * Web Push (VAPID) orchestration on top of Angular's `SwPush` — KAN-150.
 *
 * The backend sends ngsw's native `{ notification: … }` envelope and resolves
 * the click itself through `data.onActionClick`, so there is no custom
 * service worker: this service only owns permission, the local subscription
 * and its mirror in the backend, plus in-app navigation when a notification
 * is clicked while the app is open.
 *
 * Lifecycle hooks wired elsewhere:
 * - `init()`             → AppComponent, once.
 * - `syncOnStart()`      → AuthService once the session is known (cheap upsert:
 *                           refreshes `last_seen_at`, re-binds the device to the
 *                           current user, repairs worker-side renewals).
 * - `disableForLogout()` → AuthService *before* dropping the JWT.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
    private readonly httpS = inject(PushHttpService);
    private readonly swPush = inject(SwPush);
    private readonly router = inject(Router);

    /** Browser exposes the Web Push APIs (on iOS only Home-Screen PWAs do). */
    readonly isSupported = signal(this.detectSupport());
    /** Angular's worker is registered and enabled (false in `ng serve`). */
    readonly hasServiceWorker = signal(this.swPush.isEnabled);
    readonly permission = signal<NotificationPermission>(this.readPermission());
    /** Endpoint of the live local subscription, `null` when none. */
    readonly endpoint = signal<string | null>(null);
    readonly backendStatus = signal<PushBackendStatus>('unknown');

    /** Toggle ON = permission granted and a local subscription alive. */
    readonly isEnabled = computed(() => this.permission() === 'granted' && !!this.endpoint());

    private initialized = false;
    private vapidKey: string | null = null;

    /* ------------------------------------------------------------------ */
    /* Lifecycle                                                           */
    /* ------------------------------------------------------------------ */

    init() {
        if (this.initialized || !this.isSupported() || !this.swPush.isEnabled) return;
        this.initialized = true;

        // Keep the endpoint signal in sync with the worker-side subscription.
        this.swPush.subscription.subscribe(sub => this.endpoint.set(sub?.endpoint ?? null));

        // Click while the app is open: ngsw focuses the window, we route in-app
        // (no reload). With no window open ngsw itself opens `onActionClick.url`.
        this.swPush.notificationClicks.subscribe(({ notification }) => {
            const data = notification?.data as PushNotificationData | undefined;
            void this.router.navigateByUrl(PushService.resolveUrl(data));
        });

        void this.watchPermission();
    }

    /** Re-reads permission + local subscription. Safe to call anytime. */
    async refreshLocalState(): Promise<void> {
        this.permission.set(this.readPermission());
        const sub = await this.getLocalSubscription();
        this.endpoint.set(sub?.endpoint ?? null);
    }

    async syncOnStart(): Promise<void> {
        if (!this.isSupported() || !this.swPush.isEnabled) return;
        await this.refreshLocalState();
        if (!this.isEnabled()) return;
        const sub = await this.getLocalSubscription();
        if (!sub) return;
        try {
            await this.register(sub.toJSON());
        } catch {
            /* offline or backend down — next start retries */
        }
    }

    /**
     * Unregisters this device while the JWT is still valid. Never throws and
     * gives up after a short timeout so logout is never blocked.
     */
    async disableForLogout(): Promise<void> {
        if (!this.isSupported() || !this.endpoint()) return;
        const cap = new Promise<void>(resolve => setTimeout(resolve, 2000));
        await Promise.race([this.disable().catch(() => undefined), cap]);
    }

    /* ------------------------------------------------------------------ */
    /* Backend availability                                                */
    /* ------------------------------------------------------------------ */

    /** Resolves the VAPID key once; flips `backendStatus` to `unavailable` on 503. */
    async checkBackend(): Promise<PushBackendStatus> {
        if (this.vapidKey) {
            this.backendStatus.set('available');
            return 'available';
        }
        try {
            const { public_key } = await firstValueFrom(this.httpS.reqVapidPublicKey());
            this.vapidKey = public_key;
            this.backendStatus.set('available');
        } catch (err) {
            const status = (err as HttpErrorResponse)?.status;
            this.backendStatus.set(status === 503 ? 'unavailable' : 'unknown');
        }
        return this.backendStatus();
    }

    /* ------------------------------------------------------------------ */
    /* Enable / disable                                                    */
    /* ------------------------------------------------------------------ */

    /**
     * Must run from a user gesture (iOS). Throws `PushPermissionDeniedError`
     * when the user refuses; any other error means the backend registration
     * failed and the local subscription was rolled back.
     */
    async enable(): Promise<void> {
        if ((await this.checkBackend()) !== 'available' || !this.vapidKey) {
            throw new Error('push-backend-unavailable');
        }
        if (!this.swPush.isEnabled) throw new Error('push-no-service-worker');

        const existing = await this.getLocalSubscription();
        let sub: PushSubscription;
        if (existing) {
            sub = existing;
        } else {
            try {
                // Prompts for permission and subscribes in one go.
                sub = await this.swPush.requestSubscription({ serverPublicKey: this.vapidKey });
            } catch (err) {
                this.permission.set(this.readPermission());
                if (this.permission() !== 'granted') throw new PushPermissionDeniedError();
                throw err;
            }
        }
        this.permission.set(this.readPermission());

        try {
            await this.register(sub.toJSON());
        } catch (err) {
            // Don't leave a subscription the backend doesn't know about.
            if (!existing) await sub.unsubscribe().catch(() => undefined);
            throw err;
        }
        this.endpoint.set(sub.endpoint);
    }

    async disable(): Promise<void> {
        const sub = await this.getLocalSubscription();
        if (!sub) {
            this.endpoint.set(null);
            return;
        }
        try {
            await firstValueFrom(this.httpS.reqUnregister(sub.endpoint));
        } finally {
            await sub.unsubscribe().catch(() => undefined);
            this.endpoint.set(null);
        }
    }

    /** Called after the current device's row is removed server-side. */
    async dropLocalSubscription(): Promise<void> {
        const sub = await this.getLocalSubscription();
        await sub?.unsubscribe().catch(() => undefined);
        this.endpoint.set(null);
    }

    register(json: PushSubscriptionJSON): Promise<unknown> {
        const payload = this.toPayload(json);
        if (!payload) return Promise.reject(new Error('push-invalid-subscription'));
        return firstValueFrom(this.httpS.reqRegister(payload));
    }

    /* ------------------------------------------------------------------ */
    /* Helpers                                                             */
    /* ------------------------------------------------------------------ */

    /**
     * In-app destination for a notification's `data`. Always the neutral
     * `/notify` landing, which picks the mobile or desktop route; falls back
     * to whatever URL the backend put in `onActionClick.default`.
     */
    static resolveUrl(data: PushNotificationData | undefined): string {
        if (!data) return '/notify';
        if ((data.type === 'message' || data.type === 'assignment') && data.conversation_id) {
            return `/notify?type=${data.type}&conversation_id=${data.conversation_id}`;
        }
        if (data.type === 'task' && data.task_id) {
            return `/notify?type=task&task_id=${encodeURIComponent(data.task_id)}`;
        }
        if (data.type === 'call' && data.conversation_id) {
            return `/notify?type=call&conversation_id=${data.conversation_id}`;
        }
        const fallback = (data as { onActionClick?: { default?: { url?: string } } }).onActionClick
            ?.default?.url;
        return fallback || data.url || '/notify';
    }

    private toPayload(json: PushSubscriptionJSON): PushSubscriptionPayload | null {
        const { endpoint, keys } = json;
        if (!endpoint || !keys?.['p256dh'] || !keys?.['auth']) return null;
        return {
            endpoint,
            keys: { p256dh: keys['p256dh'], auth: keys['auth'] },
            user_agent: navigator.userAgent,
            platform: detectPlatform(),
        };
    }

    private async getLocalSubscription(): Promise<PushSubscription | null> {
        if (!this.isSupported() || !this.swPush.isEnabled) return null;
        try {
            return await firstValueFrom(
                this.swPush.subscription.pipe(take(1), timeout(SUBSCRIPTION_WAIT_MS))
            );
        } catch {
            return null;
        }
    }

    private detectSupport(): boolean {
        return (
            typeof window !== 'undefined' &&
            'serviceWorker' in navigator &&
            'PushManager' in window &&
            'Notification' in window
        );
    }

    private readPermission(): NotificationPermission {
        return typeof Notification !== 'undefined' ? Notification.permission : 'denied';
    }

    private async watchPermission() {
        try {
            const status = await navigator.permissions?.query({
                name: 'notifications' as PermissionName,
            });
            status?.addEventListener('change', () => void this.refreshLocalState());
        } catch {
            /* Permissions API unavailable (older Safari) — fine */
        }
    }
}

export function detectPlatform(): PushPlatform {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'web';
}
